/**
 * The button with which an answer offers its own route.
 *
 * Rendered from a `navigate` block (`guideBlocks.ts`), used by `ChatMessage` as
 * a new `case 'navigate'` in the inline renderer — in the main chat and in the
 * question panel alike; both run the same renderer.
 *
 * **It is an anchor, not a `<button>`.** The button has the house look
 * (`buttonVariants`) but the semantics of a link: `href` set, click handled via
 * `MarkdownLink`. That way right-click → „In neuem Tab öffnen", ⌘/Ctrl-click
 * and the middle mouse button work exactly as for the markdown link the same
 * answer carries in its text (text mirroring, CONTRACT.md §7.3). A `<button>`
 * with `onClick={navigate}` would have lost all of that.
 *
 * No `ExternalLink` icon: the target is in the portal and opens here. The
 * arrow points right, not diagonally out of the surface.
 *
 * Nothing is rendered if the block's address fails the check
 * (`navigateHrefFrom`) — only a button without a target would remain, and the
 * route is in the answer text.
 */

import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MarkdownLink } from '../components/markdownLinkComponent';
import { guideLocale, isNavigateBlock, navigateBlockLabel, navigateHrefFrom } from './guideBlocks';

export interface GuideNavigateBlockProps {
    /** The resource as it appeared in the payload — unchecked. */
    resource: unknown;
    className?: string;
}

export function GuideNavigateBlock({ resource, className }: GuideNavigateBlockProps) {
    const { t, i18n } = useTranslation();

    if (!isNavigateBlock(resource)) return null;
    const href = navigateHrefFrom(resource);
    if (!href) return null;

    const locale = guideLocale(i18n?.language);
    const label = navigateBlockLabel(resource, locale) || t('guide.navigate.open');

    return (
        <div className={cn('my-2 flex', className)}>
            <MarkdownLink
                href={href}
                data-testid='guide-navigate-block'
                className={cn(
                    buttonVariants({ variant: 'secondary', size: 'sm' }),
                    // `no-underline`: the answer sits in a prose area where anchors
                    // are underlined — here it is a button.
                    'gap-2 no-underline',
                )}
            >
                <span>{label}</span>
                <ArrowRight className='size-4 shrink-0' aria-hidden='true' />
            </MarkdownLink>
        </div>
    );
}
